import CheckIcon from "@mui/icons-material/Check";
import { styled } from "@mui/material";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import UserEntityMemberInvitationAPIService from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitation, {
  InvalidUserEntityMemberInvitationError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfo, { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import PasswordInput, { PasswordIssues } from "users/auth/credentials/passwords/_views/password-input";
import Password from "users/auth/credentials/passwords/password";

const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.common.white,
    textDecoration: "underline",
  },
}));
const InvitationEmailInput = styled(EmailAddressInput)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-disabled": {
    backgroundColor: theme.palette.common.white,
  },
}));
const InvitationNameInput = styled(NameInput)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: theme.palette.common.white,
  },
}));
const InvitationPasswordInput = styled(PasswordInput)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: theme.palette.common.white,
  },
}));
const InvitationAcceptButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: "#E19941",
  color: theme.palette.common.white,
  marginTop: theme.spacing(2),
  "&.MuiButton-root": {
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: "#FFB949",
    },
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.3)",
      boxShadow: "none",
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
}));
const FormElements = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

type InvitationAcceptanceFormError = string | PasswordIssues;

type InvitationAcceptanceFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  password?: PasswordIssues;
};

type UserEntityMemberInvitationAcceptanceFormProps = {
  className?: string;
  invitation: UserEntityMemberInvitation;
};

export default function UserEntityMemberInvitationAcceptanceForm(
  props: Readonly<UserEntityMemberInvitationAcceptanceFormProps>,
) {
  const { className, invitation } = props;

  const [invitationName, setInvitationName] = useState<Name>(invitation.registrationInfo.name);
  const [invitationPassword, setInvitationPassword] = useState<Password>();
  const [formErrors, setFormErrors] = useState<InvitationAcceptanceFormErrors>();
  const [saving, setSaving] = useState<boolean>(false);
  const invitationEmail = invitation?.invitedEmail;
  const session = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (!invitation?.registrationInfo?.name) return;
    setInvitationName(invitation?.registrationInfo?.name);
  }, [invitation?.registrationInfo?.name]);

  function rebuildInvitation(
    emailAddress?: EmailAddress,
    name?: Name,
    password?: Password,
    errors?: InvitationAcceptanceFormErrors,
  ): UserEntityMemberInvitation | undefined {
    if (!invitation || !formValid(errors) || !emailAddress || !name || !password) return;

    try {
      const invitationRegistrationInfo = RegistrationInfo.create({
        params: {
          name,
          email: emailAddress,
          password,
        },
        options: { silenceSchemaErrors: true },
      });
      const updatedInvitation = UserEntityMemberInvitation.create({
        userEntityInfo: invitation.userEntityInfo,
        invitedEmail: invitation.invitedEmail,
        registrationInfo: invitationRegistrationInfo,
        id: invitation.id,
      });
      return updatedInvitation;
    } catch (error) {
      if (error instanceof InvalidUserEntityMemberInvitationError || error instanceof InvalidRegistrationInfoError) {
        let errors: InvitationAcceptanceFormErrors = {};
        errors.general = error.message;
        return;
      }
      throw error;
    }
  }

  async function handleAcceptInvitation() {
    setSaving(true);

    try {
      const builtInvite = rebuildInvitation(invitationEmail, invitationName, invitationPassword);
      if (!builtInvite) throw new InvalidUserEntityMemberInvitationError("Invitation is invalid");

      const service = new UserEntityMemberInvitationAPIService(session);
      await service.acceptUserEntityMemberInvitation(builtInvite, session);
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      const message = (error as Error)?.message ?? "";
      if (message.includes("Zod")) {
        enqueueSnackbar("There was an error processing your invitation.", { variant: "error" });
      } else {
        enqueueSnackbar(message, { variant: "error" });
      }
    } finally {
      setSaving(false);
    }
  }

  function formValid(errors?: InvitationAcceptanceFormErrors): boolean {
    errors ??= formErrors;

    return (
      invitationEmail !== undefined &&
      invitationName !== undefined &&
      invitationPassword !== undefined &&
      !errors?.general &&
      !errors?.email &&
      !errors?.name &&
      !errors?.password
    );
  }

  function updateFormErrors(type: keyof InvitationAcceptanceFormErrors, error?: InvitationAcceptanceFormError) {
    let errors: InvitationAcceptanceFormErrors = { ...formErrors };

    if (type === "password") {
      errors.password = error as PasswordIssues;
    } else if (type === "email") {
      errors.email = error as string;
    } else if (type === "name") {
      errors.name = error as string;
    } else {
      errors.general = error as string;
    }

    setFormErrors(errors);
    return errors;
  }

  function invitationAlreadyAcceptedMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Already Accepted</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation has already been accepted.
          <br />
          If you believe this is in error please contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .<br />
          You can close this tab
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  if (!invitation) return null;
  if (invitation.status === InvitationStatus.Accepted) return invitationAlreadyAcceptedMessage();

  return (
    <InvitationForm className={className}>
      <InvitationFormTitle variant="h4">Accept Invitation</InvitationFormTitle>
      <InvitationFormInstructions>
        Enter the following to register as an administrator on Attorney Hub account for{" "}
        {invitation.userEntityInfo.entityName?.value}:
      </InvitationFormInstructions>
      <form>
        <InvitationEmailInput variant="filled" disabled emailAddress={invitationEmail} />
        <FormElements>
          <InvitationNameInput
            variant="filled"
            nameValue={invitationName}
            onChange={(updatedName?: Name, error?: string) => {
              if (updatedName) setInvitationName(updatedName);
              const errors = updateFormErrors("name", error);
              rebuildInvitation(invitationEmail, updatedName, invitationPassword, errors);
            }}
          />
          <InvitationPasswordInput
            variant="filled"
            confirm={true}
            password={invitationPassword}
            onChange={(updatedPassword?: Password, error?: PasswordIssues) => {
              if (updatedPassword) setInvitationPassword(updatedPassword);
              const errors = updateFormErrors("password", error);
              rebuildInvitation(invitationEmail, invitationName, updatedPassword, errors);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === KeyboardKey.Enter && formValid()) {
                void handleAcceptInvitation();
              }
            }}
          />
        </FormElements>
        <InvitationAcceptButton
          variant="contained"
          color="primary"
          disabled={saving || !formValid()}
          loading={saving}
          startIcon={<CheckIcon />}
          onClick={handleAcceptInvitation}
        >
          Accept Invitation
        </InvitationAcceptButton>
      </form>
    </InvitationForm>
  );
}
